import type {
	ISupportRecipientSuggestion,
	ISupportRecipientSuggestions,
} from '../../../../queries/Support/useSupportRecipientSuggestions';
import { getInactiveUserStatusBadge, getUserReferenceName } from '../../../../utils/Support/supportHelpers';

/** The one address in a comma-separated field that the caret sits in. */
export interface RecipientFragment {
	start: number;
	end: number;
	text: string;
}

export interface RecipientOption {
	email: string;
	label: string;
	/** Badge for a user who is not active yet, so an agent picks one knowingly. */
	statusLabel?: string;
	statusColor?: 'gray' | 'red' | 'yellow';
}

export interface RecipientGroup {
	heading: string;
	options: RecipientOption[];
}

interface BuildRecipientGroupsOptions {
	accountName?: string;
	/** Addresses already in the composer's address fields; never offered again. */
	excludedEmails: string[];
	query: string;
	suggestions?: ISupportRecipientSuggestions;
}

const TEAMMATES_HEADING = 'Teammates';
const ACCOUNT_FALLBACK_HEADING = 'Account';

export const getRecipientFragment = (value: string, caret: number): RecipientFragment => {
	const bounded = Math.max(0, Math.min(caret, value.length));
	const start = value.lastIndexOf(',', bounded - 1) + 1;
	const nextSeparator = value.indexOf(',', bounded);
	const end = nextSeparator === -1 ? value.length : nextSeparator;
	return { end, start, text: value.slice(start, end).trim() };
};

/**
 * Swaps the caret's fragment for a picked address, leaving the caret after the separator so the next
 * address can be typed straight away.
 */
export const applyRecipientCompletion = (
	value: string,
	fragment: RecipientFragment,
	email: string
): { caret: number; value: string } => {
	const before = value.slice(0, fragment.start);
	const remainder = value.slice(fragment.end).replace(/^[\s,]+/, '');
	// The fragment's own leading space belongs to the separator before it, not to the address.
	const prefix = before.length > 0 && !/\s$/.test(before) ? `${before} ` : before;
	const completed = `${prefix}${email}, `;
	return { caret: completed.length, value: `${completed}${remainder}` };
};

const matchesQuery = (option: RecipientOption, query: string): boolean => {
	const needle = query.trim().toLowerCase();
	if (!needle) return true;
	return option.label.toLowerCase().includes(needle) || option.email.toLowerCase().includes(needle);
};

const toOption = (suggestion: ISupportRecipientSuggestion): RecipientOption => {
	const email = suggestion.email.trim();
	const badge = getInactiveUserStatusBadge(suggestion.activationStatus);
	return {
		email,
		label: getUserReferenceName({ ...suggestion, email }) ?? email,
		statusColor: badge?.color,
		statusLabel: badge?.label,
	};
};

/**
 * Narrowing by `query` is safe because the server matched a prefix of what is in the field now, and it
 * keeps the list from lagging a keystroke behind the debounce.
 */
export const buildRecipientGroups = ({
	accountName,
	excludedEmails,
	query,
	suggestions,
}: BuildRecipientGroupsOptions): RecipientGroup[] => {
	const seen = new Set(excludedEmails.map(email => email.trim().toLowerCase()).filter(Boolean));
	const sources: { heading: string; suggestions: ISupportRecipientSuggestion[] }[] = [
		{ heading: TEAMMATES_HEADING, suggestions: suggestions?.teammates ?? [] },
		{ heading: accountName?.trim() || ACCOUNT_FALLBACK_HEADING, suggestions: suggestions?.accountUsers ?? [] },
	];

	const groups: RecipientGroup[] = [];
	for (const source of sources) {
		const options: RecipientOption[] = [];
		for (const suggestion of source.suggestions) {
			if (!suggestion.email?.trim()) continue;
			const option = toOption(suggestion);
			const normalizedEmail = option.email.toLowerCase();
			if (seen.has(normalizedEmail)) continue;
			if (!matchesQuery(option, query)) continue;
			seen.add(normalizedEmail);
			options.push(option);
		}
		if (options.length > 0) groups.push({ heading: source.heading, options });
	}

	return groups;
};
